import type { RoleScope } from "./role";
import type { IconName } from "../components/icon/Icon";
import type { TranslationKey } from "./i18n";
import { keyFromSelector } from "i18next";
import { flatMap, isEmpty, keyBy } from "lodash-es";

export const PERMISSION_TYPES = [
  "CREATE_SUBMISSION",
  "READ_SUBMISSION",
  "UPDATE_SUBMISSION",
  "DELETE_SUBMISSION",
  "READ_REGISTRATION",
  "DELETE_REGISTRATION",
  "CREATE_TASK",
  "READ_TASK",
  "UPDATE_TASK",
  "DELETE_TASK",
  "CREATE_USER",
  "READ_USER",
  "UPDATE_USER",
  "DELETE_USER",
  "READ_COLLECTION",
  "CREATE_FORM",
  "UPDATE_FORM",
  "ARCHIVE_FORM",
  "RESTORE_FORM",
  "PREVIEW_FORM",
  "ADD_FORM",
  "REMOVE_FORM",
  "CREATE_FOLDER",
  "UPDATE_FOLDER",
  "DELETE_FOLDER",
  "READ_REGISTRATION_FILE",
  "READ_DATA_SOURCE",
  "CREATE_DATA_SOURCE",
  "UPDATE_DATA_SOURCE",
  "DELETE_DATA_SOURCE",
  "READ_RESOURCE",
  "READ_WEBHOOK",
  "CREATE_WEBHOOK",
  "UPDATE_WEBHOOK",
  "DELETE_WEBHOOK",
  "TRANSFER_OWNERSHIP",
  "CREATE_CUSTOMERS",
  "CUSTOMER_USER",
  "FORM_USER",
  "FOLDER_USER",
  "MANAGE_ACCOUNT",
  "MANAGE_CREDITS",
  "MANAGE_DATA_SOURCES",
  "MANAGE_FORMS",
  "MANAGE_TASKS",
  "MANAGE_REGISTRATIONS",
  "MANAGE_USERS",
  "MANAGE_WEBHOOKS",
  "MANAGE_SSO",
  "MANAGE_SENDING_DOMAIN",
  "MANAGE_TEMPLATES",
] as const;

export type PermissionType = (typeof PERMISSION_TYPES)[number];

export type PermissionCategory =
  | "IAM"
  | "FOLDERS_AND_FORMS"
  | "DATA_SOURCES"
  | "BILLING"
  | "CONNECTIONS"
  | "WEBHOOKS"
  | "RESOURCES"
  | "FOLDERS"
  | "FORMS"
  | "SUBMISSIONS"
  | "TASKS"
  | "SYSTEM"
  | "LEGACY";

export type PermissionDefinition = {
  type: PermissionType;
  scope: RoleScope;
  icon: IconName;
  name: TranslationKey;
  description: TranslationKey;
};

export const PERMISSION_DEFINITIONS: { category: PermissionCategory; permissions: PermissionDefinition[] }[] = [
  {
    category: "IAM",
    permissions: [
      {
        type: "MANAGE_USERS",
        scope: "CUSTOMER",
        icon: "users",
        name: keyFromSelector(($) => $.PERMISSION_MANAGE_USERS_NAME),
        description: keyFromSelector(($) => $.PERMISSION_MANAGE_USERS_DESCRIPTION),
      },
      {
        type: "READ_USER",
        scope: "CUSTOMER",
        icon: "viewUser",
        name: keyFromSelector(($) => $.PERMISSION_READ_USER_NAME),
        description: keyFromSelector(($) => $.PERMISSION_READ_USER_DESCRIPTION),
      },
      {
        type: "CREATE_USER",
        scope: "CUSTOMER",
        icon: "addUser",
        name: keyFromSelector(($) => $.PERMISSION_CREATE_USER_NAME),
        description: keyFromSelector(($) => $.PERMISSION_CREATE_USER_DESCRIPTION),
      },
      {
        type: "UPDATE_USER",
        scope: "CUSTOMER",
        icon: "editUser",
        name: keyFromSelector(($) => $.PERMISSION_UPDATE_USER_NAME),
        description: keyFromSelector(($) => $.PERMISSION_UPDATE_USER_DESCRIPTION),
      },
      {
        type: "DELETE_USER",
        scope: "CUSTOMER",
        icon: "deleteUser",
        name: keyFromSelector(($) => $.PERMISSION_DELETE_USER_NAME),
        description: keyFromSelector(($) => $.PERMISSION_DELETE_USER_DESCRIPTION),
      },
    ],
  },
  {
    category: "FOLDERS_AND_FORMS",
    permissions: [
      {
        type: "MANAGE_FORMS",
        scope: "CUSTOMER",
        icon: "folderOpen",
        name: keyFromSelector(($) => $.PERMISSION_MANAGE_FORMS_NAME),
        description: keyFromSelector(($) => $.PERMISSION_MANAGE_FORMS_DESCRIPTION),
      },
      {
        type: "CREATE_FOLDER",
        scope: "CUSTOMER",
        icon: "folderAdd",
        name: keyFromSelector(($) => $.PERMISSION_CREATE_FOLDER_NAME),
        description: keyFromSelector(($) => $.PERMISSION_CREATE_FOLDER_DESCRIPTION),
      },
      {
        type: "CREATE_FORM",
        scope: "CUSTOMER",
        icon: "documentAdd",
        name: keyFromSelector(($) => $.PERMISSION_CREATE_FORM_NAME),
        description: keyFromSelector(($) => $.PERMISSION_CREATE_FORM_DESCRIPTION),
      },
      {
        type: "FOLDER_USER",
        scope: "FOLDER",
        icon: "folderAccess",
        name: keyFromSelector(($) => $.PERMISSION_FOLDER_USER_NAME),
        description: keyFromSelector(($) => $.PERMISSION_FOLDER_USER_DESCRIPTION),
      },
    ],
  },
  {
    category: "DATA_SOURCES",
    permissions: [
      {
        type: "MANAGE_DATA_SOURCES",
        scope: "CUSTOMER",
        icon: "database",
        name: keyFromSelector(($) => $.PERMISSION_MANAGE_DATA_SOURCES_NAME),
        description: keyFromSelector(($) => $.PERMISSION_MANAGE_DATA_SOURCES_DESCRIPTION),
      },
      {
        type: "READ_DATA_SOURCE",
        scope: "CUSTOMER",
        icon: "viewDataSource",
        name: keyFromSelector(($) => $.PERMISSION_READ_DATA_SOURCE_NAME),
        description: keyFromSelector(($) => $.PERMISSION_READ_DATA_SOURCE_DESCRIPTION),
      },
      {
        type: "CREATE_DATA_SOURCE",
        scope: "CUSTOMER",
        icon: "createDataSource",
        name: keyFromSelector(($) => $.PERMISSION_CREATE_DATA_SOURCE_NAME),
        description: keyFromSelector(($) => $.PERMISSION_CREATE_DATA_SOURCE_DESCRIPTION),
      },
      {
        type: "UPDATE_DATA_SOURCE",
        scope: "CUSTOMER",
        icon: "updateDatasource",
        name: keyFromSelector(($) => $.PERMISSION_UPDATE_DATA_SOURCE_NAME),
        description: keyFromSelector(($) => $.PERMISSION_UPDATE_DATA_SOURCE_DESCRIPTION),
      },
      {
        type: "DELETE_DATA_SOURCE",
        scope: "CUSTOMER",
        icon: "deleteDataSource",
        name: keyFromSelector(($) => $.PERMISSION_DELETE_DATA_SOURCE_NAME),
        description: keyFromSelector(($) => $.PERMISSION_DELETE_DATA_SOURCE_DESCRIPTION),
      },
    ],
  },
  {
    category: "BILLING",
    permissions: [
      {
        type: "MANAGE_CREDITS",
        scope: "CUSTOMER",
        icon: "creditCard",
        name: keyFromSelector(($) => $.PERMISSION_MANAGE_CREDITS_NAME),
        description: keyFromSelector(($) => $.PERMISSION_MANAGE_CREDITS_DESCRIPTION),
      },
    ],
  },
  {
    category: "CONNECTIONS",
    permissions: [
      {
        type: "MANAGE_SENDING_DOMAIN",
        scope: "CUSTOMER",
        icon: "mail",
        name: keyFromSelector(($) => $.PERMISSION_MANAGE_SENDING_DOMAIN_NAME),
        description: keyFromSelector(($) => $.PERMISSION_MANAGE_SENDING_DOMAIN_DESCRIPTION),
      },
      {
        type: "MANAGE_SSO",
        scope: "CUSTOMER",
        icon: "key",
        name: keyFromSelector(($) => $.PERMISSION_MANAGE_SSO_NAME),
        description: keyFromSelector(($) => $.PERMISSION_MANAGE_SSO_DESCRIPTION),
      },
    ],
  },
  {
    category: "WEBHOOKS",
    permissions: [
      {
        type: "MANAGE_WEBHOOKS",
        scope: "CUSTOMER",
        icon: "webhook",
        name: keyFromSelector(($) => $.PERMISSION_MANAGE_WEBHOOKS_NAME),
        description: keyFromSelector(($) => $.PERMISSION_MANAGE_WEBHOOKS_DESCRIPTION),
      },
      {
        type: "READ_WEBHOOK",
        scope: "CUSTOMER",
        icon: "viewWebhook",
        name: keyFromSelector(($) => $.PERMISSION_READ_WEBHOOK_NAME),
        description: keyFromSelector(($) => $.PERMISSION_READ_WEBHOOK_DESCRIPTION),
      },
      {
        type: "CREATE_WEBHOOK",
        scope: "CUSTOMER",
        icon: "createWebhook",
        name: keyFromSelector(($) => $.PERMISSION_CREATE_WEBHOOK_NAME),
        description: keyFromSelector(($) => $.PERMISSION_CREATE_WEBHOOK_DESCRIPTION),
      },
      {
        type: "UPDATE_WEBHOOK",
        scope: "CUSTOMER",
        icon: "editWebhook",
        name: keyFromSelector(($) => $.PERMISSION_UPDATE_WEBHOOK_NAME),
        description: keyFromSelector(($) => $.PERMISSION_UPDATE_WEBHOOK_DESCRIPTION),
      },
      {
        type: "DELETE_WEBHOOK",
        scope: "CUSTOMER",
        icon: "deleteWebhook",
        name: keyFromSelector(($) => $.PERMISSION_DELETE_WEBHOOK_NAME),
        description: keyFromSelector(($) => $.PERMISSION_DELETE_WEBHOOK_DESCRIPTION),
      },
    ],
  },
  {
    category: "RESOURCES",
    permissions: [
      {
        type: "READ_RESOURCE",
        scope: "CUSTOMER",
        icon: "resources",
        name: keyFromSelector(($) => $.PERMISSION_READ_RESOURCE_NAME),
        description: keyFromSelector(($) => $.PERMISSION_READ_RESOURCE_DESCRIPTION),
      },
    ],
  },
  {
    category: "FOLDERS",
    permissions: [
      {
        type: "ADD_FORM",
        scope: "FOLDER",
        icon: "addForm",
        name: keyFromSelector(($) => $.PERMISSION_ADD_FORM_NAME),
        description: keyFromSelector(($) => $.PERMISSION_ADD_FORM_DESCRIPTION),
      },
      {
        type: "UPDATE_FOLDER",
        scope: "FOLDER",
        icon: "editFolder",
        name: keyFromSelector(($) => $.PERMISSION_UPDATE_FOLDER_NAME),
        description: keyFromSelector(($) => $.PERMISSION_UPDATE_FOLDER_DESCRIPTION),
      },
      {
        type: "DELETE_FOLDER",
        scope: "FOLDER",
        icon: "deleteFolder",
        name: keyFromSelector(($) => $.PERMISSION_DELETE_FOLDER_NAME),
        description: keyFromSelector(($) => $.PERMISSION_DELETE_FOLDER_DESCRIPTION),
      },
    ],
  },
  {
    category: "FORMS",
    permissions: [
      {
        type: "UPDATE_FORM",
        scope: "FORM",
        icon: "editForm",
        name: keyFromSelector(($) => $.PERMISSION_UPDATE_FORM_NAME),
        description: keyFromSelector(($) => $.PERMISSION_UPDATE_FORM_DESCRIPTION),
      },
      {
        type: "RESTORE_FORM",
        scope: "FORM",
        icon: "restoreForm",
        name: keyFromSelector(($) => $.PERMISSION_RESTORE_FORM_NAME),
        description: keyFromSelector(($) => $.PERMISSION_RESTORE_FORM_DESCRIPTION),
      },
      {
        type: "PREVIEW_FORM",
        scope: "FORM",
        icon: "previewForm",
        name: keyFromSelector(($) => $.PERMISSION_PREVIEW_FORM_NAME),
        description: keyFromSelector(($) => $.PERMISSION_PREVIEW_FORM_DESCRIPTION),
      },
      {
        type: "REMOVE_FORM",
        scope: "FOLDER",
        icon: "removeForm",
        name: keyFromSelector(($) => $.PERMISSION_REMOVE_FORM_NAME),
        description: keyFromSelector(($) => $.PERMISSION_REMOVE_FORM_DESCRIPTION),
      },
      {
        type: "ARCHIVE_FORM",
        scope: "FORM",
        icon: "deleteForm",
        name: keyFromSelector(($) => $.PERMISSION_ARCHIVE_FORM_NAME),
        description: keyFromSelector(($) => $.PERMISSION_ARCHIVE_FORM_DESCRIPTION),
      },
      {
        type: "FORM_USER",
        scope: "FORM",
        icon: "formAccess",
        name: keyFromSelector(($) => $.PERMISSION_FORM_USER_NAME),
        description: keyFromSelector(($) => $.PERMISSION_FORM_USER_DESCRIPTION),
      },
    ],
  },
  {
    category: "SUBMISSIONS",
    permissions: [
      {
        type: "MANAGE_REGISTRATIONS",
        scope: "CUSTOMER",
        icon: "documentText",
        name: keyFromSelector(($) => $.PERMISSION_MANAGE_REGISTRATIONS_NAME),
        description: keyFromSelector(($) => $.PERMISSION_MANAGE_REGISTRATIONS_DESCRIPTION),
      },
      {
        type: "CREATE_SUBMISSION",
        scope: "FORM",
        icon: "deleteForm",
        name: keyFromSelector(($) => $.PERMISSION_CREATE_SUBMISSION_NAME),
        description: keyFromSelector(($) => $.PERMISSION_CREATE_SUBMISSION_DESCRIPTION),
      },
      {
        type: "READ_SUBMISSION",
        scope: "FORM",
        icon: "deleteForm",
        name: keyFromSelector(($) => $.PERMISSION_READ_SUBMISSION_NAME),
        description: keyFromSelector(($) => $.PERMISSION_READ_SUBMISSION_DESCRIPTION),
      },
      {
        type: "UPDATE_SUBMISSION",
        scope: "FORM",
        icon: "deleteForm",
        name: keyFromSelector(($) => $.PERMISSION_UPDATE_SUBMISSION_NAME),
        description: keyFromSelector(($) => $.PERMISSION_UPDATE_SUBMISSION_DESCRIPTION),
      },
      {
        type: "DELETE_SUBMISSION",
        scope: "FORM",
        icon: "deleteForm",
        name: keyFromSelector(($) => $.PERMISSION_DELETE_SUBMISSION_NAME),
        description: keyFromSelector(($) => $.PERMISSION_DELETE_SUBMISSION_DESCRIPTION),
      },
    ],
  },
  {
    category: "TASKS",
    permissions: [
      {
        type: "MANAGE_TASKS",
        scope: "CUSTOMER",
        icon: "clipboardCopy",
        name: keyFromSelector(($) => $.PERMISSION_MANAGE_TASKS_NAME),
        description: keyFromSelector(($) => $.PERMISSION_MANAGE_TASKS_DESCRIPTION),
      },
      {
        type: "READ_TASK",
        scope: "FORM",
        icon: "deleteForm",
        name: keyFromSelector(($) => $.PERMISSION_READ_TASK_NAME),
        description: keyFromSelector(($) => $.PERMISSION_READ_TASK_DESCRIPTION),
      },
      {
        type: "CREATE_TASK",
        scope: "FORM",
        icon: "deleteForm",
        name: keyFromSelector(($) => $.PERMISSION_CREATE_TASK_NAME),
        description: keyFromSelector(($) => $.PERMISSION_CREATE_TASK_DESCRIPTION),
      },
      {
        type: "UPDATE_TASK",
        scope: "FORM",
        icon: "deleteForm",
        name: keyFromSelector(($) => $.PERMISSION_UPDATE_TASK_NAME),
        description: keyFromSelector(($) => $.PERMISSION_UPDATE_TASK_DESCRIPTION),
      },
      {
        type: "DELETE_TASK",
        scope: "FORM",
        icon: "deleteForm",
        name: keyFromSelector(($) => $.PERMISSION_DELETE_TASK_NAME),
        description: keyFromSelector(($) => $.PERMISSION_DELETE_TASK_DESCRIPTION),
      },
    ],
  },
  {
    category: "SYSTEM",
    permissions: [
      {
        type: "CUSTOMER_USER",
        scope: "CUSTOMER",
        icon: "userPermission",
        name: keyFromSelector(($) => $.PERMISSION_CUSTOMER_USER_NAME),
        description: keyFromSelector(($) => $.PERMISSION_CUSTOMER_USER_DESCRIPTION),
      },
      {
        type: "TRANSFER_OWNERSHIP",
        scope: "CUSTOMER",
        icon: "userPermission",
        name: keyFromSelector(($) => $.PERMISSION_TRANSFER_OWNERSHIP_NAME),
        description: keyFromSelector(($) => $.PERMISSION_TRANSFER_OWNERSHIP_DESCRIPTION),
      },
      {
        type: "MANAGE_ACCOUNT",
        scope: "CUSTOMER",
        icon: "userPermission",
        name: keyFromSelector(($) => $.PERMISSION_MANAGE_ACCOUNT_NAME),
        description: keyFromSelector(($) => $.PERMISSION_MANAGE_ACCOUNT_DESCRIPTION),
      },
      {
        type: "CREATE_CUSTOMERS",
        scope: "CUSTOMER",
        icon: "userPermission",
        name: keyFromSelector(($) => $.PERMISSION_CREATE_CUSTOMERS_NAME),
        description: keyFromSelector(($) => $.PERMISSION_CREATE_CUSTOMERS_DESCRIPTION),
      },
    ],
  },
  {
    category: "LEGACY",
    permissions: [
      {
        type: "MANAGE_TEMPLATES",
        scope: "CUSTOMER",
        icon: "userPermission",
        name: keyFromSelector(($) => $.PERMISSION_MANAGE_TEMPLATES_NAME),
        description: keyFromSelector(($) => $.PERMISSION_MANAGE_TEMPLATES_DESCRIPTION),
      },
      {
        type: "READ_COLLECTION",
        scope: "FORM",
        icon: "userPermission",
        name: keyFromSelector(($) => $.PERMISSION_READ_COLLECTION_NAME),
        description: keyFromSelector(($) => $.PERMISSION_READ_COLLECTION_DESCRIPTION),
      },
      {
        type: "READ_REGISTRATION",
        scope: "FORM",
        icon: "userPermission",
        name: keyFromSelector(($) => $.PERMISSION_READ_REGISTRATION_NAME),
        description: keyFromSelector(($) => $.PERMISSION_READ_REGISTRATION_DESCRIPTION),
      },
      {
        type: "DELETE_REGISTRATION",
        scope: "FORM",
        icon: "userPermission",
        name: keyFromSelector(($) => $.PERMISSION_DELETE_REGISTRATION_NAME),
        description: keyFromSelector(($) => $.PERMISSION_DELETE_REGISTRATION_DESCRIPTION),
      },
      {
        type: "READ_REGISTRATION_FILE",
        scope: "FORM",
        icon: "star",
        name: keyFromSelector(($) => $.PERMISSION_READ_REGISTRATION_FILE_NAME),
        description: keyFromSelector(($) => $.PERMISSION_READ_REGISTRATION_FILE_DESCRIPTION),
      },
    ],
  },
] as const;

export const PERMISSION_DEFINITIONS_BY_TYPE = keyBy(flatMap(PERMISSION_DEFINITIONS, "permissions"), "type") as Record<
  PermissionType,
  PermissionDefinition
>;

const HIDDEN_PERMISSION_CATEGORIES: PermissionCategory[] = ["SYSTEM", "LEGACY"];

export const HIDDEN_PERMISSION_TYPES = new Set<PermissionType>(
  PERMISSION_DEFINITIONS.filter(({ category }) => HIDDEN_PERMISSION_CATEGORIES.includes(category)).flatMap(
    ({ permissions }) => permissions.map((p) => p.type),
  ),
);

export const PERMISSION_CATEGORY_TRANSLATION_KEYS: Record<PermissionCategory, TranslationKey> = {
  IAM: keyFromSelector(($) => $.PERMISSION_CATEGORY_IAM),
  FOLDERS_AND_FORMS: keyFromSelector(($) => $.PERMISSION_CATEGORY_FOLDERS_AND_FORMS),
  DATA_SOURCES: keyFromSelector(($) => $.PERMISSION_CATEGORY_DATA_SOURCES),
  BILLING: keyFromSelector(($) => $.PERMISSION_CATEGORY_BILLING),
  CONNECTIONS: keyFromSelector(($) => $.PERMISSION_CATEGORY_CONNECTIONS),
  WEBHOOKS: keyFromSelector(($) => $.PERMISSION_CATEGORY_WEBHOOKS),
  RESOURCES: keyFromSelector(($) => $.PERMISSION_CATEGORY_RESOURCES),
  FOLDERS: keyFromSelector(($) => $.PERMISSION_CATEGORY_FOLDERS),
  FORMS: keyFromSelector(($) => $.PERMISSION_CATEGORY_FORMS),
  SUBMISSIONS: keyFromSelector(($) => $.PERMISSION_CATEGORY_SUBMISSIONS),
  TASKS: keyFromSelector(($) => $.PERMISSION_CATEGORY_TASKS),
  SYSTEM: keyFromSelector(($) => $.PERMISSION_CATEGORY_SYSTEM),
  LEGACY: keyFromSelector(($) => $.PERMISSION_CATEGORY_LEGACY),
};

export const permissionsForScope = (scope: RoleScope) =>
  PERMISSION_DEFINITIONS.map((categoryGroup) => ({
    category: categoryGroup.category,
    permissions: categoryGroup.permissions.filter((p) => p.scope === scope),
  })).filter(({ permissions }) => !isEmpty(permissions));
